import { useCallback } from 'react';
import { isGreaterWithThreshold } from './utility';

/**
 * Custom hook to manage the navigation of a carousel component.
 *
 * @param {React.RefObject<HTMLDivElement>} containerRef - Ref to the carousel container element.
 * @param {boolean} canScroll - Indicates if the carousel can scroll.
 * @param {number} paddingX - Horizontal padding for the carousel items.
 * @returns {object} - An object containing navigation handlers for next and previous actions.
 */
const useCarouselNavigation = (
	containerRef: React.RefObject<HTMLDivElement | null>,
	canScroll: boolean,
	paddingX: number
) => {
	const handleClickNext = useCallback(() => {
		const slider = containerRef.current;
		if (!slider || !canScroll) return;

		let position = 0;
		const items = slider.querySelectorAll<HTMLElement>(':scope > .carousel-item');
		for (let i = 0; i < items.length; i += 1) {
			const item = items[i];
			const itemOffset = item.offsetLeft - paddingX;

			if (isGreaterWithThreshold(itemOffset, slider.scrollLeft)) {
				position = itemOffset;
				break;
			}
		}

		slider.scrollTo({ left: position, behavior: 'smooth' });
		// The warning is generally safe to ignore in this case (because useRef is stable by default)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canScroll, paddingX]);

	const handleClickPrevious = useCallback(() => {
		const slider = containerRef.current;
		if (!slider || !canScroll) return;

		let position = 0;
		const items = slider.querySelectorAll<HTMLElement>(':scope > .carousel-item');

		for (let i = 0; i < items.length; i += 1) {
			const item = items[i];
			const itemOffset = item.offsetLeft - paddingX;

			if (isGreaterWithThreshold(slider.scrollLeft, itemOffset)) {
				position = itemOffset;
			}
		}

		slider.scrollTo({ left: position, behavior: 'smooth' });
		// The warning is generally safe to ignore in this case (because useRef is stable by default)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canScroll, paddingX]);

	return {
		handleClickNext,
		handleClickPrevious
	};
};

export default useCarouselNavigation;
